import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getCategoriesByServiceSlug, Category } from '../api/products.api';
import LoadingSpinner from '../components/LoadingSpinner';

// Service data structure matching HomePage
const servicesData: Record<string, {
  title: string;
  icon: string;
  description: string;
  image?: string;
}> = {
  'customised-jersey': {
    title: 'Customised Jersey',
    icon: 'bi-badge-ad',
    description: 'ক্লাব ও টিম জার্সি',
    image: '/customised_Jersey_sub.jpeg'
  },
  'apparel-production': {
    title: 'Apparel Production',
    icon: 'bi-scissors',
    description: 'নিজের টিমের জন্য পছন্দের জার্সি ও টি-শার্ট অর্ডার করুন।'
  },
  'sports-item': {
    title: 'Sports Item',
    icon: 'bi-basketball',
    description: 'সবধরনের স্পোর্টস আইটেম হাব',
    image: '/SPORTS_ITEMS_sub.jpeg'
  },
  'award-and-trophy': {
    title: 'Award and Trophy',
    icon: 'bi-trophy-fill',
    description: 'সকল ধরনের এওয়ার্ড'
  },
  'hunting-program': {
    title: 'Hunting Program',
    icon: 'bi-bullseye',
    description: 'দেশব্যাপী প্রতিভা অন্বেষণ'
  },
  'sports-events': {
    title: 'Sports Events',
    icon: 'bi-trophy',
    description: 'জাতীয় ও প্রতিযোগিতামূলক স্পোর্টস ইভেন্ট'
  },
  'dealership-program': {
    title: 'Dealership Program',
    icon: 'bi-handshake',
    description: 'দেশব্যাপী তরুণ উদ্যোক্তা তৈরীর পদক্ষেপ।',
    image: '/Dealership_sub.jpeg'
  }
};

// Services that link to events instead of products
const EVENT_BASED_SERVICES = ['hunting-program', 'dealership-program'];

const ServiceCategoryPage: React.FC = () => {
  const { serviceSlug } = useParams<{ serviceSlug: string }>();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCategories = async () => {
      if (!serviceSlug || !servicesData[serviceSlug]) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await getCategoriesByServiceSlug(serviceSlug);
        setCategories(response.data || []);
      } catch (err: any) {
        console.error('Error fetching categories:', err);
        if (err.response?.status === 404) {
          // Service not found or no categories
          setCategories([]);
        } else if (err.response?.status === 400) {
          // Invalid service slug
          setError('Invalid service');
        } else {
          // Other errors
          setError('Failed to load categories');
          setCategories([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [serviceSlug]);
  
  if (!serviceSlug || !servicesData[serviceSlug]) {
    return (
      <div className="container py-5">
        <div className="alert alert-secondary">
          <h4>Service Not Found</h4>
          <p className="mb-0">The service you're looking for doesn't exist.</p>
          <Link to="/" className="btn btn-outline-secondary mt-3">
            Go to Home
          </Link>
        </div>
      </div>
    );
  }

  const service = servicesData[serviceSlug];
  const isEventBased = EVENT_BASED_SERVICES.includes(serviceSlug);

  if (loading) {
    return (
      <div className="container py-5">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="mb-4">
        <Link to="/" className="btn btn-outline-secondary btn-sm mb-4">
          <i className="bi bi-arrow-left me-1"></i>
          Back to Home
        </Link>
        
        {/* Service Image or Icon */}
        {service.image ? (
          <div className="service-category-image-container mb-4">
            <img 
              src={service.image} 
              alt={service.title}
              className="service-category-image"
              loading="lazy"
            />
          </div>
        ) : (
        <div className="d-flex align-items-center gap-3 mb-3">
          <div className="service-icon" style={{ fontSize: '3rem', color: 'var(--accent-primary)' }}>
            <i className={`bi ${service.icon}`} />
          </div>
          <div>
            <h1 className="h3 section-title mb-1">{service.title}</h1>
            <p className="text-muted-soft mb-0">{service.description}</p>
          </div>
        </div>
        )}
        
        {/* Title and Description (shown below image if image exists) */}
        {service.image && (
          <div className="mb-3">
            <h1 className="h3 section-title mb-2">{service.title}</h1>
            <p className="text-muted-soft mb-0">{service.description}</p>
          </div>
        )}
      </div>

      <div className="card card-surface mb-4">
        <div className="card-body p-4">
          <h2 className="h5 mb-4">{isEventBased ? 'Select an Event Category' : 'Select a Category'}</h2>
          {error && (
            <div className="alert alert-warning mb-3">
              <p className="mb-0">{error}</p>
            </div>
          )}
          {categories.length === 0 && !loading ? (
            <div className="alert alert-secondary">
              <p className="mb-0">No categories available for this service yet.</p>
            </div>
          ) : (
          <div className="row g-3">
              {categories.map((category, index) => {
              const linkTo = isEventBased 
                  ? `/events?category=${encodeURIComponent(category.name)}`
                  : `/jerseys?category=${encodeURIComponent(category.name)}`;
              
              return (
                  <div key={category.id} className="col-12 col-md-6 col-lg-4">
                  <Link
                    to={linkTo}
                    className="card card-surface h-100 text-decoration-none"
                    style={{ transition: 'transform 0.2s, box-shadow 0.2s' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '';
                    }}
                  >
                    <div className="card-body d-flex align-items-center gap-3">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: '50px',
                          height: '50px',
                          backgroundColor: 'var(--accent-primary)',
                          color: '#ffffff',
                          fontSize: '1.25rem',
                          flexShrink: 0
                        }}
                      >
                        {index + 1}
                      </div>
                      <div className="flex-grow-1">
                          <h6 className="mb-0 text-white">{category.name}</h6>
                          {category.description && (
                            <p className="text-muted-soft small mb-0 mt-1">{category.description}</p>
                          )}
                          {!isEventBased && !category.description && (
                          <p className="text-muted-soft small mb-0 mt-1">View Products</p>
                        )}
                      </div>
                      <i className="bi bi-arrow-right text-muted-soft"></i>
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceCategoryPage;
